import React, { useState, useEffect } from 'react';
import { Form, Input, Select, Button, Card, message, InputNumber, DatePicker, Row, Col, Tag } from 'antd';
import { SaveOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { productApi, batchApi, photoTypeApi, categoryApi } from '../../services/api';
import PhotoUpload from '../../components/PhotoUpload';
import dayjs from 'dayjs';

const { TextArea } = Input;

const ProductForm = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [batchMap, setBatchMap] = useState({}); // id -> batch object
  const [product, setProduct] = useState(null);
  const [allProductPhotoTypes, setAllProductPhotoTypes] = useState([]); // 所有货品级照片类型
  const [allowedProductPhotoTypes, setAllowedProductPhotoTypes] = useState([]); // 当前品类允许的货品级照片类型
  const [categoryMap, setCategoryMap] = useState({}); // categoryName -> category object
  const navigate = useCompanyNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  useEffect(() => {
    const init = async () => {
      const [batchList, ptRes, catRes] = await Promise.all([
        batchApi.all().then(r => r.data || []).catch(() => []),
        photoTypeApi.all().then(r => r.data || []).catch(() => []),
        categoryApi.all().then(r => r.data || []).catch(() => []),
      ]);

      // 批次数据
      setBatches(batchList.map(b => ({ label: `${b.batch_no || b.batchNo} - ${b.name}`, value: b.id })));
      const bMap = {};
      batchList.forEach(b => { bMap[b.id] = b; });
      setBatchMap(bMap);

      // 照片类型
      const productTypes = ptRes.filter(t => t.level === 'product' && t.status !== 0);
      productTypes.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setAllProductPhotoTypes(productTypes);

      // 品类数据
      const cMap = {};
      catRes.forEach(c => { cMap[c.name] = c; });
      setCategoryMap(cMap);

      // 编辑模式：加载货品数据
      if (isEdit) {
        try {
          const res = await productApi.detail(id);
          setProduct(res.data);
          form.setFieldsValue({
            productCode: res.data.product_code,
            batchId: res.data.batch_id,
            name: res.data.name,
            material: res.data.material,
            weight: res.data.weight,
            dimensions: res.data.dimensions,
            craftsMan: res.data.crafts_man,
            finishDate: res.data.finish_date ? dayjs(res.data.finish_date) : null,
            description: res.data.description,
          });
          // 根据批次品类确定允许的照片类型
          if (res.data.batch_id && bMap[res.data.batch_id]?.category) {
            const catName = bMap[res.data.batch_id].category;
            const cat = cMap[catName];
            if (cat?.photo_types) {
              const catPhotoTypeIds = cat.photo_types.split(',').map(Number).filter(n => !isNaN(n));
              const filtered = productTypes.filter(pt => catPhotoTypeIds.includes(pt.id));
              setAllowedProductPhotoTypes(filtered);
            } else {
              setAllowedProductPhotoTypes(productTypes);
            }
          } else {
            setAllowedProductPhotoTypes(productTypes);
          }
        } catch {
          navigate('/admin/products');
        }
      } else {
        setAllowedProductPhotoTypes(productTypes);
      }
    };
    init();
  }, [id]);

  // 批次选择变化时更新允许的照片类型
  const handleBatchChange = (batchId) => {
    const batch = batchMap[batchId];
    if (batch && batch.category) {
      const cat = categoryMap[batch.category];
      if (cat?.photo_types) {
        const catPhotoTypeIds = cat.photo_types.split(',').map(Number).filter(n => !isNaN(n));
        const filtered = allProductPhotoTypes.filter(pt => catPhotoTypeIds.includes(pt.id));
        setAllowedProductPhotoTypes(filtered);
      } else {
        setAllowedProductPhotoTypes(allProductPhotoTypes);
      }
    } else {
      setAllowedProductPhotoTypes(allProductPhotoTypes);
    }
  };

  const fetchProduct = async () => {
    try {
      const res = await productApi.detail(id);
      setProduct(res.data);
    } catch {
      // ignore
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const data = {
        ...values,
        finishDate: values.finishDate ? values.finishDate.format('YYYY-MM-DD') : null,
      };

      if (isEdit) {
        await productApi.update(id, data);
        message.success('更新成功');
      } else {
        const res = await productApi.create(data);
        message.success('创建成功');
        // 跳转到编辑页面以便上传照片
        navigate(`/admin/products/${res.data.id}/edit`);
      }
    } finally {
      setLoading(false);
    }
  };

  // 当前批次品类名（useWatch 监听表单值变化，切换批次时实时刷新）
  const currentBatchId = Form.useWatch('batchId', form) || product?.batch_id;
  const currentBatch = batchMap[currentBatchId];
  const currentCategoryName = currentBatch?.category;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admin/products')}>
          返回
        </Button>
        <h2 style={{ margin: 0 }}>{isEdit ? '编辑货品' : '新增货品'}</h2>
      </div>

      <Row gutter={24}>
        <Col xs={24} lg={16}>
          <Card>
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              autoComplete="off"
            >
              <Form.Item
                name="productCode"
                label="货品编码"
                rules={[{ required: true, message: '请输入货品编码' }]}
              >
                <Input placeholder="请输入唯一的货品编码" disabled={isEdit} />
              </Form.Item>

              <Form.Item
                name="batchId"
                label="所属批次"
                rules={[{ required: true, message: '请选择所属批次' }]}
              >
                <Select
                  placeholder="请选择批次"
                  options={batches}
                  showSearch
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleBatchChange}
                />
              </Form.Item>

              {currentCategoryName && (
                <div style={{ marginBottom: 16, marginTop: -8 }}>
                  <Tag color="blue">品类：{currentCategoryName}</Tag>
                  <span style={{ color: '#999', fontSize: 12 }}>货品品类跟随批次，可上传照片由品类决定</span>
                </div>
              )}

              <Form.Item
                name="name"
                label="货品名称"
                rules={[{ required: true, message: '请输入货品名称' }]}
              >
                <Input placeholder="请输入货品名称" />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="material" label="材质">
                    <Select
                      placeholder="选择材质"
                      allowClear
                      options={[
                        { label: '羊脂白玉', value: '羊脂白玉' },
                        { label: '白玉', value: '白玉' },
                        { label: '青玉', value: '青玉' },
                        { label: '墨玉', value: '墨玉' },
                        { label: '碧玉', value: '碧玉' },
                        { label: '黄玉', value: '黄玉' },
                        { label: '糖玉', value: '糖玉' },
                        { label: '其他', value: '其他' },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="weight" label="重量(g)">
                    <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="0.00" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="dimensions" label="尺寸">
                    <Input placeholder="如: 50×30×10mm" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="craftsMan" label="工艺师">
                <Input placeholder="请输入工艺师姓名" />
              </Form.Item>

              <Form.Item name="finishDate" label="完工日期">
                <DatePicker style={{ width: '100%' }} placeholder="选择完工日期" />
              </Form.Item>

              <Form.Item name="description" label="描述/工艺说明">
                <TextArea rows={4} placeholder="请输入货品描述或工艺说明" />
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                  size="large"
                  style={{ background: '#8B7355' }}
                >
                  {isEdit ? '保存修改' : '创建货品'}
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {isEdit && (
          <Col xs={24} lg={8}>
            <Card title="货品照片">
              {currentCategoryName && (
                <div style={{ marginBottom: 12, fontSize: 12, color: '#666' }}>
                  品类「{currentCategoryName}」允许上传的照片类型
                </div>
              )}
              <PhotoUpload
                productId={id}
                batchId={product?.batch_id}
                photoType={allowedProductPhotoTypes.length > 0 ? allowedProductPhotoTypes[0].id : 3}
                productPhotoTypes={allowedProductPhotoTypes}
                photosByType={product?.photosByType || {}}
                finishedPhotos={product?.finishedPhotos || []}
                certificatePhotos={product?.certificatePhotos || []}
                onUploadSuccess={fetchProduct}
              />
            </Card>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default ProductForm;
